import { useCallback, useRef, useEffect, useState } from 'react';
import { getStorage, unifiedSetDoc, unifiedGetDoc, unifiedDeleteDoc } from '@watchlist/shared';

/**
 * Interface for review draft data
 */
export interface ReviewDraft {
  movieId: string;
  rating: number | null;
  review: string;
  visibility: 'anyone' | 'only-me';
  watchedDate?: string; // ISO string — preferred
  watchedDatePrecision?: 'day' | 'month' | 'year';
  // Legacy fields kept for backward compat with existing stored drafts
  watchedYear?: number;
  watchedMonth?: number | null;
  createdAt: string;
  updatedAt: string;
  // Movie metadata for displaying in drafts list
  movieTitle?: string;
  moviePoster?: string;
  movieYear?: string;
}

/**
 * Format relative time for "last saved" display
 */
export const formatRelativeTime = (date: Date | string | null): string => {
  if (!date) return '';

  const now = new Date();
  const savedDate = typeof date === 'string' ? new Date(date) : date;
  const diffMs = now.getTime() - savedDate.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);

  if (diffSecs < 5) return 'just now';
  if (diffSecs < 60) return `${diffSecs}s ago`;
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return savedDate.toLocaleDateString();
};

/**
 * Hook for managing review drafts with autosave functionality.
 *
 * Strategy:
 * - During typing: local-only save, debounced. No Firestore writes, no
 *   isSaving state churn — both add main-thread work that throttles iOS
 *   keyboard responsiveness on Capacitor.
 * - On dialog close (saveDraftImmediate): flush to local + Firestore.
 *
 * @param userId - Current user ID
 * @param localDebounceMs - Debounce delay for local save (default: 300ms)
 */
export const useReviewDraft = (
  userId: string,
  localDebounceMs: number = 300
) => {
  const localTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const firestoreTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const storage = getStorage();

  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  // Kept for API compatibility with consumers that read draftHook.isSaving.
  // Always false now — we no longer toggle saving state during typing.
  const isSaving = false;

  const getDraftKey = useCallback((movieId: string): string => {
    return `review_draft_${userId}_${movieId}`;
  }, [userId]);

  /**
   * Load draft from local storage first, then check Firestore
   * @returns Draft object or null if not found
   */
  const loadDraft = useCallback(async (movieId: string): Promise<ReviewDraft | null> => {
    if (!userId) {
      console.log('❌ loadDraft: No userId, skipping load');
      return null;
    }

    try {
      const key = getDraftKey(movieId);
      console.log('📝 loadDraft: Checking storage for key:', key);

      // Try local storage first (fastest)
      const localDraftJson = await storage.getItem(key);
      console.log('📝 loadDraft: Local storage result:', localDraftJson ? 'found draft' : 'no draft');
      let localDraft: ReviewDraft | null = null;

      if (localDraftJson) {
        localDraft = JSON.parse(localDraftJson) as ReviewDraft;
        console.log(`📝 Local draft found for movie ${movieId}`);
      }

      // Try Firestore (may have newer data from another device)
      try {
        const firestoreDoc = await unifiedGetDoc(`users/${userId}/reviewDrafts`, movieId);
        if (firestoreDoc.exists) {
          const firestoreDraft = firestoreDoc.data as ReviewDraft;
          console.log(`📝 Firestore draft found for movie ${movieId}`);

          // Use whichever is newer
          if (!localDraft) {
            // Only Firestore draft exists, save to local
            await storage.setItem(key, JSON.stringify(firestoreDraft));
            return firestoreDraft;
          }

          const localDate = new Date(localDraft.updatedAt);
          const firestoreDate = new Date(firestoreDraft.updatedAt);

          if (firestoreDate > localDate) {
            // Firestore is newer, update local
            await storage.setItem(key, JSON.stringify(firestoreDraft));
            return firestoreDraft;
          }
        }
      } catch (firestoreError) {
        console.warn('Failed to check Firestore draft:', firestoreError);
        // Continue with local draft if Firestore fails
      }

      return localDraft;
    } catch (error) {
      console.error('Failed to load draft:', error);
      return null;
    }
  }, [getDraftKey, storage, userId]);

  /**
   * Save draft to LOCAL storage only, debounced. Firestore sync is deferred to
   * saveDraftImmediate (called on dialog close) — keeping the bridge call out
   * of the typing path is critical for keyboard responsiveness on Capacitor iOS.
   * No setIsSaving toggle here either: state churn during typing was causing
   * mid-keystroke re-renders that throttled iOS backspace key-repeat.
   */
  const saveDraft = useCallback((
    draft: Omit<ReviewDraft, 'createdAt' | 'updatedAt'>,
    movieMetadata?: { title?: string; poster?: string; year?: string }
  ): void => {
    if (!userId) return;
    // Drafts require review text under the new flow. Rating/date alone are
    // not a draft. If a legacy rating-only draft exists for this movie from
    // the old behavior, leave it — `loadDraft` consumers filter at read time
    // (MovieDetail) and explicit deletes happen via deleteDraft.
    if (!draft.review?.trim()) return;

    const now = new Date();
    const nowIso = now.toISOString();

    if (localTimeoutRef.current) {
      clearTimeout(localTimeoutRef.current);
    }

    localTimeoutRef.current = setTimeout(async () => {
      try {
        const key = getDraftKey(draft.movieId);

        const existingDraftJson = await storage.getItem(key);
        let createdAt: string;
        let existingMetadata: { movieTitle?: string; moviePoster?: string; movieYear?: string } = {};

        if (existingDraftJson) {
          const existingDraft = JSON.parse(existingDraftJson) as ReviewDraft;
          createdAt = existingDraft.createdAt;
          existingMetadata = {
            movieTitle: existingDraft.movieTitle,
            moviePoster: existingDraft.moviePoster,
            movieYear: existingDraft.movieYear,
          };
        } else {
          createdAt = nowIso;
        }

        const fullDraft: ReviewDraft = {
          ...draft,
          createdAt,
          updatedAt: nowIso,
          movieTitle: movieMetadata?.title || existingMetadata.movieTitle,
          moviePoster: movieMetadata?.poster || existingMetadata.moviePoster,
          movieYear: movieMetadata?.year || existingMetadata.movieYear,
        };

        await storage.setItem(key, JSON.stringify(fullDraft));
        setLastSavedAt(now);
      } catch (error) {
        console.error('Failed to save draft locally:', error);
      }
    }, localDebounceMs);
  }, [getDraftKey, storage, userId, localDebounceMs]);

  /**
   * Save draft immediately to both local and Firestore
   * Useful for unmount or when user closes the page
   */
  const saveDraftImmediate = useCallback(async (
    draft: Omit<ReviewDraft, 'createdAt' | 'updatedAt'>,
    movieMetadata?: { title?: string; poster?: string; year?: string }
  ): Promise<void> => {
    if (!userId) {
      console.log('❌ saveDraftImmediate: No userId, skipping save');
      return;
    }
    if (!draft.review?.trim()) {
      console.log('📝 saveDraftImmediate: empty review, skipping (rating-only is not a draft)');
      return;
    }
    console.log('📝 saveDraftImmediate called for movie:', draft.movieId);

    // Clear any pending saves
    if (localTimeoutRef.current) {
      clearTimeout(localTimeoutRef.current);
      localTimeoutRef.current = null;
    }
    if (firestoreTimeoutRef.current) {
      clearTimeout(firestoreTimeoutRef.current);
      firestoreTimeoutRef.current = null;
    }

    const now = new Date();
    const nowIso = now.toISOString();

    try {
      const key = getDraftKey(draft.movieId);

      // Load existing draft to preserve createdAt
      const existingDraftJson = await storage.getItem(key);
      let createdAt: string;
      let existingMetadata: { movieTitle?: string; moviePoster?: string; movieYear?: string } = {};

      if (existingDraftJson) {
        const existingDraft = JSON.parse(existingDraftJson) as ReviewDraft;
        createdAt = existingDraft.createdAt;
        existingMetadata = {
          movieTitle: existingDraft.movieTitle,
          moviePoster: existingDraft.moviePoster,
          movieYear: existingDraft.movieYear,
        };
      } else {
        createdAt = nowIso;
      }

      const fullDraft: ReviewDraft = {
        ...draft,
        createdAt,
        updatedAt: nowIso,
        movieTitle: movieMetadata?.title || existingMetadata.movieTitle,
        moviePoster: movieMetadata?.poster || existingMetadata.moviePoster,
        movieYear: movieMetadata?.year || existingMetadata.movieYear,
      };

      // Save to both local and Firestore simultaneously
      await Promise.all([
        storage.setItem(key, JSON.stringify(fullDraft)),
        unifiedSetDoc(`users/${userId}/reviewDrafts`, draft.movieId, fullDraft, true).catch(err => {
          console.warn('Firestore sync failed on immediate save:', err);
        }),
      ]);

      setLastSavedAt(now);
      console.log(`💾 Draft saved immediately for movie ${draft.movieId}`);
    } catch (error) {
      console.error('Failed to save draft immediately:', error);
      throw error;
    }
  }, [getDraftKey, storage, userId]);

  /**
   * Delete draft from both local storage and Firestore
   */
  const deleteDraft = useCallback(async (movieId: string): Promise<void> => {
    if (!userId) return;

    // Clear any pending saves
    if (localTimeoutRef.current) {
      clearTimeout(localTimeoutRef.current);
      localTimeoutRef.current = null;
    }
    if (firestoreTimeoutRef.current) {
      clearTimeout(firestoreTimeoutRef.current);
      firestoreTimeoutRef.current = null;
    }

    try {
      const key = getDraftKey(movieId);

      // Delete from both local and Firestore
      await Promise.all([
        storage.removeItem(key),
        unifiedDeleteDoc(`users/${userId}/reviewDrafts`, movieId).catch(err => {
          console.warn('Firestore delete failed:', err);
        }),
      ]);

      setLastSavedAt(null);
      console.log(`🗑️ Draft deleted for movie ${movieId}`);
    } catch (error) {
      console.error('Failed to delete draft:', error);
      throw error;
    }
  }, [getDraftKey, storage, userId]);

  /**
   * Check if draft is stale (older than 7 days)
   */
  const isDraftStale = useCallback((draft: ReviewDraft): boolean => {
    const now = new Date();
    const updatedAt = new Date(draft.updatedAt);
    const ageInDays = (now.getTime() - updatedAt.getTime()) / (1000 * 60 * 60 * 24);
    return ageInDays > 7;
  }, []);

  /**
   * Check if draft is newer than a given timestamp
   * Useful for conflict resolution (draft vs saved review)
   */
  const isDraftNewer = useCallback((draft: ReviewDraft, comparisonDate: Date | string): boolean => {
    const draftDate = new Date(draft.updatedAt);
    const compareDate = typeof comparisonDate === 'string'
      ? new Date(comparisonDate)
      : comparisonDate;
    return draftDate > compareDate;
  }, []);

  /**
   * Check if draft has meaningful content (not empty)
   */
  const hasDraftContent = useCallback((draft: ReviewDraft): boolean => {
    return !!draft.review?.trim();
  }, []);

  /**
   * Cleanup: flush any pending saves on unmount
   */
  useEffect(() => {
    return () => {
      if (localTimeoutRef.current) {
        clearTimeout(localTimeoutRef.current);
      }
      if (firestoreTimeoutRef.current) {
        clearTimeout(firestoreTimeoutRef.current);
      }
    };
  }, []);

  return {
    loadDraft,
    saveDraft,
    saveDraftImmediate,
    deleteDraft,
    isDraftStale,
    isDraftNewer,
    hasDraftContent,
    lastSavedAt,
    isSaving,
    formatRelativeTime,
  };
};
